import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { useState } from 'react';
import { toast } from 'sonner';

import { EditDialog } from '../../../EditDialog';
import { EditDynamicSelectField } from '../../../EditDynamicSelectField';
import type { ContactMilitaryInfo } from '../queries/contactSummaryQuery';
import { GET_CONTACT_SUMMARY } from '../queries/contactSummaryQuery';

interface FieldMapping {
  apiField: string;
  formField: keyof ContactMilitaryInfo;
}

const MILITARY_INFO_FIELDS: FieldMapping[] = [
  { apiField: 'militaryAffiliation', formField: 'militaryAffiliation' },
  { apiField: 'militaryDuty', formField: 'militaryDuty' }
];

function getChangedValues(
  fields: FieldMapping[],
  current: ContactMilitaryInfo,
  initial: ContactMilitaryInfo
): Record<string, null | string> {
  const changes: Record<string, null | string> = {};

  for (const { apiField, formField } of fields) {
    const currentVal = current[formField];
    const initialVal = initial[formField];

    if (currentVal !== initialVal) {
      changes[apiField] = currentVal === '' ? null : currentVal;
    }
  }

  return changes;
}

const UPDATE_CONTACT = gql`
  mutation UpdateContact($id: ID!, $values: ContactInput!) {
    updateContact(id: $id, values: $values) {
      ok
      contact {
        id
        militaryAffiliation
        militaryDuty
      }
    }
  }
`;

export interface EditMilitaryInfoDialogProps {
  contactId: string;
  initialValue: ContactMilitaryInfo;
  isOpen: boolean;
  onChange: (data: ContactMilitaryInfo) => void;
  onOpenChange: (open: boolean) => void;
  onSave: () => void;
  value: ContactMilitaryInfo;
}

export function EditMilitaryInfoDialog({
  contactId,
  initialValue,
  isOpen,
  onChange,
  onOpenChange,
  onSave,
  value
}: EditMilitaryInfoDialogProps) {
  const [isSaving, setIsSaving] = useState(false);

  const [updateContact] = useMutation(UPDATE_CONTACT, {
    refetchQueries: [
      { query: GET_CONTACT_SUMMARY, variables: { id: contactId } }
    ]
  });

  const changes = getChangedValues(MILITARY_INFO_FIELDS, value, initialValue);
  const isDirty = Object.keys(changes).length > 0;

  const updateField = <K extends keyof ContactMilitaryInfo>(
    field: K,
    fieldValue: ContactMilitaryInfo[K]
  ) => {
    onChange({ ...value, [field]: fieldValue });
  };

  const handleSave = async () => {
    if (!isDirty) {
      onSave();
      return;
    }

    setIsSaving(true);

    try {
      const response = await updateContact({
        variables: { id: contactId, values: changes }
      });

      const data = response.data as {
        updateContact?: { ok?: boolean };
      };

      if (data.updateContact?.ok === false) {
        toast.error('Failed to save military information');
      } else {
        toast.success('Military information saved successfully');
        onSave();
      }
    } catch (error) {
      console.error('Error saving military information:', error);
      toast.error('Failed to save military information');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <EditDialog
      isDirty={isDirty}
      isOpen={isOpen}
      isSaving={isSaving}
      onOpenChange={onOpenChange}
      onSave={handleSave}
      title="Edit Military Information"
    >
      <div className="grid grid-cols-2 gap-6">
        <EditDynamicSelectField
          fieldName="military_affiliation"
          label="Military Affiliation"
          model="res.partner"
          onChange={v => {
            updateField('militaryAffiliation', v);
          }}
          value={value.militaryAffiliation}
        />
        <EditDynamicSelectField
          fieldName="military_duty"
          label="Military Duty"
          model="res.partner"
          onChange={v => {
            updateField('militaryDuty', v);
          }}
          value={value.militaryDuty}
        />
      </div>
    </EditDialog>
  );
}
